import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalculatorIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const Taxes = () => {
  const [taxRate, setTaxRate] = useState('');
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { user, isAdminOrManager } = useAuth();

  console.log('Taxes component - user:', user, 'isAdminOrManager:', isAdminOrManager);


  // Load current tax rate on component mount
  useEffect(() => {
    if (isAdminOrManager) {
      fetchTaxRate();
    }
  }, [isAdminOrManager]);

  const fetchTaxRate = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      const response = await axios.get('/api/settings/financial/tax', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data && response.data.data) {
        const taxData = response.data.data;
        setTaxRate(taxData.rate || '');
        setTaxEnabled(taxData.enabled || false);
      }
    } catch (error) {
      console.error('Error fetching tax rate:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view tax settings');
      } else if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status !== 404) {
        setError('Failed to load tax settings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaxRateChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string for clearing
    if (value === '') {
      setTaxRate('');
      return;
    }

    // Validate numerical input with up to 2 decimal places
    const regex = /^\d{0,2}(\.\d{0,2})?$/;
    if (regex.test(value) && parseFloat(value) <= 99.99) {
      setTaxRate(value);
    }
  };

  const handleSave = async () => {
    if (taxEnabled && (!taxRate || taxRate === '')) {
      setError('Please enter a tax rate when tax is enabled');
      return;
    }

    let numericRate = 0;
    if (taxEnabled) {
      numericRate = parseFloat(taxRate);
      if (isNaN(numericRate) || numericRate < 0 || numericRate > 99.99) {
        setError('Tax rate must be between 0.00 and 99.99');
        return;
      }
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      await axios.put('/api/settings/financial/tax', {
        enabled: taxEnabled,
        rate: numericRate
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccessMessage(`Tax settings saved successfully (${taxEnabled ? 'enabled' : 'disabled'})`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving tax settings:', error);
      setError('Failed to save tax settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    fetchTaxRate(); // Reset to original values
    setError('');
    setSuccessMessage('');
  };

  // Show loading state while checking permissions
  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Show error if user doesn't have permission
  if (!isAdminOrManager) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">You do not have permission to access tax settings.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Tax Settings
          </h2>
          <p className="mt-1 text-gray-600">
            Configure tax rates and calculation rules
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-6">
            <CalculatorIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Tax Rate Management</h3>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            {/* Tax Enable/Disable Toggle */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={(e) => setTaxEnabled(e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Enable Tax Collection
                </span>
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Check this box to apply taxes to reservations and services
              </p>
            </div>

            {/* Tax Rate Input - Only show when enabled */}
            {taxEnabled && (
              <div>
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="taxRate"
                    value={taxRate}
                    onChange={handleTaxRateChange}
                    placeholder="Enter tax rate (e.g., 8.75)"
                    className="block w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-8"
                    disabled={isLoading}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">%</span>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Enter a tax rate between 0.00% and 99.99% (up to 2 decimal places)
                </p>
              </div>
            )}

            {/* Status indicator */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${taxEnabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-sm font-medium text-gray-900">
                  Tax Status: <span className={taxEnabled ? 'text-green-600' : 'text-gray-600'}>
                    {taxEnabled ? `Enabled (${taxRate || '0'}%)` : 'Disabled'}
                  </span>
                </span>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Taxes;